import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/features/auth/AuthContext';
import { startOfWeek, format, subWeeks, addDays } from 'date-fns';

export interface Habit {
  id: string;
  user_id: string;
  title: string;
  description: string;
  help_text: string | null;
  unit: 'count' | 'minutes' | 'hours' | 'currency' | 'yes_no';
  target_per_week: number;
  kpi_type: string | null;
  active: boolean;
  sort_order: number;
}

export interface HabitLog {
  id: string;
  user_id: string;
  habit_id: string;
  week_start: string;
  count: number;
}

export function getWeekStart(date: Date = new Date(), weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 1): string {
  return format(startOfWeek(date, { weekStartsOn }), 'yyyy-MM-dd');
}

export function getWeekEnd(weekStartStr: string): Date {
  return addDays(new Date(weekStartStr + 'T00:00:00'), 6);
}

export function parseTimeInput(input: string): number {
  const s = input.trim().toLowerCase();
  const colonMatch = s.match(/^(\d+):(\d{2})$/);
  if (colonMatch) return parseInt(colonMatch[1]) * 60 + parseInt(colonMatch[2]);
  const hoursMatch = s.match(/^(\d+\.?\d*)\s*(h|hr|hrs|hour|hours)$/);
  if (hoursMatch) return Math.round(parseFloat(hoursMatch[1]) * 60);
  const minsMatch = s.match(/^(\d+\.?\d*)\s*(m|min|mins|minute|minutes)$/);
  if (minsMatch) return Math.round(parseFloat(minsMatch[1]));
  const numMatch = s.match(/^(\d+\.?\d*)$/);
  if (numMatch) return Math.round(parseFloat(numMatch[1]) * 60);
  return 0;
}

export function formatMinutes(minutes: number): string {
  if (minutes === 0) return '0';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function formatHours(hours: number): string {
  if (hours === 0) return '0';
  return hours === 1 ? '1 hr' : `${hours} hrs`;
}

export function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString()}`;
}

const ACTION_VERBS = new Set(['analyze', 'submit', 'make', 'find', 'call', 'send', 'write', 'review', 'complete', 'attend', 'contact', 'schedule', 'visit']);

export function buildDynamicTitle(habit: Habit): string {
  const { title, target_per_week, unit } = habit;
  if (unit === 'yes_no') return title;
  if (unit === 'minutes') {
    const hrs = target_per_week / 60;
    if (target_per_week < 60) return `${title} for ${target_per_week} ${target_per_week === 1 ? 'minute' : 'minutes'}`;
    if (Number.isInteger(hrs)) return `${title} for ${hrs} ${hrs === 1 ? 'hour' : 'hours'}`;
    return `${title} for ${hrs.toFixed(1)} hours`;
  }
  if (unit === 'hours') return `${title} for ${target_per_week} ${target_per_week === 1 ? 'hour' : 'hours'}`;
  if (unit === 'currency') return `${title} (${formatCurrency(target_per_week)}/wk)`;
  const words = title.split(' ');
  if (ACTION_VERBS.has(words[0].toLowerCase()) && words.length >= 2) {
    const filtered = words.filter((w, i) => !['an', 'a', 'the'].includes(w.toLowerCase()) || i === words.length - 1);
    const last = filtered[filtered.length - 1];
    const prefix = filtered.slice(0, -1).join(' ');
    return `${prefix} ${target_per_week} ${last}`;
  }
  return `${target_per_week} ${title}`;
}

const DEFAULT_HABITS: Omit<Habit, 'id' | 'user_id'>[] = [
  {
    title: 'Prospect for Leads',
    description: 'Drive for dollars, find wholesalers, network with agents etc.',
    help_text: null,
    unit: 'minutes',
    target_per_week: 120,
    kpi_type: null,
    active: true,
    sort_order: 0,
  },
  {
    title: 'Analyze Deals',
    description: 'Run the numbers using the calculator.',
    help_text: null,
    unit: 'count',
    target_per_week: 10,
    kpi_type: 'deals_analyzed',
    active: true,
    sort_order: 1,
  },
  {
    title: 'Submit an Offer',
    description: 'Make a real offer on a qualifying property.',
    help_text: null,
    unit: 'count',
    target_per_week: 1,
    kpi_type: 'offers_made',
    active: true,
    sort_order: 2,
  },
  {
    title: 'Investor Conversations',
    description: 'Talk to mentors, agents, wholesalers, or fellow investors.',
    help_text: null,
    unit: 'count',
    target_per_week: 2,
    kpi_type: null,
    active: true,
    sort_order: 3,
  },
];

export function useHabitTracker() {
  const { session } = useAuth();
  const userId = session?.user?.id;

  const [weekStartDay, setWeekStartDayState] = useState<0 | 1 | 2 | 3 | 4 | 5 | 6>(1);
  const [habits, setHabits] = useState<Habit[]>([]);
  const [logs, setLogs] = useState<HabitLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [setupError, setSetupError] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<string>('');
  const seedingRef = useRef(false);

  const currentWeek = useMemo(() => getWeekStart(new Date(), weekStartDay), [weekStartDay]);
  const isCurrentWeek = selectedWeek === currentWeek;

  const fetchData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);

    const { data: settingsData } = await supabase
      .from('user_habit_settings')
      .select('week_start_day')
      .eq('user_id', userId)
      .maybeSingle();

    const wsd = (settingsData?.week_start_day ?? 1) as 0 | 1 | 2 | 3 | 4 | 5 | 6;
    setWeekStartDayState(wsd);
    const cw = getWeekStart(new Date(), wsd);
    setSelectedWeek(prev => prev || cw);

    const { data: habitData } = await supabase
      .from('habits')
      .select('*')
      .eq('user_id', userId)
      .eq('active', true)
      .order('sort_order');

    let activeHabits = (habitData ?? []) as Habit[];

    if (activeHabits.length === 0 && !seedingRef.current) {
      seedingRef.current = true;
      const toInsert = DEFAULT_HABITS.map(h => ({ ...h, user_id: userId }));
      const { data: inserted, error: insertError } = await supabase.from('habits').insert(toInsert).select();
      seedingRef.current = false;
      if (insertError) {
        console.error('[useHabitTracker] failed to seed default habits:', insertError);
        setSetupError(true);
      } else {
        activeHabits = (inserted ?? []) as Habit[];
        setSetupError(false);
      }
    }
    setHabits(activeHabits);

    const yearStart = `${new Date().getFullYear()}-01-01`;
    const { data: logData } = await supabase
      .from('habit_logs')
      .select('*')
      .eq('user_id', userId)
      .gte('week_start', yearStart);
    setLogs((logData ?? []) as HabitLog[]);

    setLoading(false);
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getCountForWeek = useCallback((habitId: string, week: string): number => {
    return logs.find(l => l.habit_id === habitId && l.week_start === week)?.count ?? 0;
  }, [logs]);

  const setCount = useCallback(async (habitId: string, newCount: number) => {
    if (!userId || !isCurrentWeek) return;
    setLogs(prev => {
      const idx = prev.findIndex(l => l.habit_id === habitId && l.week_start === currentWeek);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], count: newCount };
        return updated;
      }
      return [...prev, { id: crypto.randomUUID(), user_id: userId, habit_id: habitId, week_start: currentWeek, count: newCount }];
    });
    await supabase.from('habit_logs').upsert(
      { user_id: userId, habit_id: habitId, week_start: currentWeek, count: newCount },
      { onConflict: 'user_id,habit_id,week_start' }
    );
  }, [userId, isCurrentWeek, currentWeek]);

  const increment = useCallback((habitId: string) => {
    return setCount(habitId, getCountForWeek(habitId, currentWeek) + 1);
  }, [getCountForWeek, currentWeek, setCount]);

  const decrement = useCallback((habitId: string) => {
    return setCount(habitId, Math.max(0, getCountForWeek(habitId, currentWeek) - 1));
  }, [getCountForWeek, currentWeek, setCount]);

  const logMinutes = useCallback((habitId: string, additionalMinutes: number) => {
    return setCount(habitId, Math.max(0, getCountForWeek(habitId, currentWeek) + additionalMinutes));
  }, [getCountForWeek, currentWeek, setCount]);

  const updateHabit = useCallback(async (id: string, updates: Partial<Pick<Habit, 'title' | 'description' | 'help_text' | 'unit' | 'target_per_week' | 'sort_order'>>) => {
    if (!userId) return;
    setHabits(prev => prev.map(h => h.id === id ? { ...h, ...updates } : h));
    await supabase.from('habits').update(updates).eq('id', id).eq('user_id', userId);
  }, [userId]);

  const reorderHabits = useCallback(async (id: string, direction: 'up' | 'down') => {
    if (!userId) return;
    const sorted = [...habits].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(h => h.id === id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const a = sorted[idx];
    const b = sorted[swapIdx];
    const aNewOrder = b.sort_order;
    const bNewOrder = a.sort_order;

    setHabits(prev => prev.map(h => {
      if (h.id === a.id) return { ...h, sort_order: aNewOrder };
      if (h.id === b.id) return { ...h, sort_order: bNewOrder };
      return h;
    }));

    await Promise.all([
      supabase.from('habits').update({ sort_order: aNewOrder }).eq('id', a.id).eq('user_id', userId),
      supabase.from('habits').update({ sort_order: bNewOrder }).eq('id', b.id).eq('user_id', userId),
    ]);
  }, [userId, habits]);

  const reorderHabitsToOrder = useCallback(async (reorderedHabits: Habit[]) => {
    if (!userId) return;
    const updates = reorderedHabits.map((h, idx) => ({ ...h, sort_order: idx }));
    setHabits(updates);
    await Promise.all(
      updates.map(h =>
        supabase.from('habits').update({ sort_order: h.sort_order }).eq('id', h.id).eq('user_id', userId)
      )
    );
  }, [userId]);

  const addHabit = useCallback(async (title: string, description: string, target: number, unit: 'count' | 'minutes' | 'hours' | 'currency' | 'yes_no' = 'count') => {
    if (!userId) return;
    const maxOrder = habits.length > 0 ? Math.max(...habits.map(h => h.sort_order)) + 1 : 0;
    const { data } = await supabase
      .from('habits')
      .insert({ user_id: userId, title, description, target_per_week: target, unit, active: true, sort_order: maxOrder })
      .select()
      .single();
    if (data) setHabits(prev => [...prev, data as Habit]);
  }, [userId, habits]);

  const removeHabit = useCallback(async (id: string) => {
    if (!userId) return;
    setHabits(prev => prev.filter(h => h.id !== id));
    await supabase.from('habits').update({ active: false }).eq('id', id).eq('user_id', userId);
  }, [userId]);

  const resetToDefaults = useCallback(async () => {
    if (!userId) return;
    await supabase.from('habits').update({ active: false }).eq('user_id', userId);
    const toInsert = DEFAULT_HABITS.map(h => ({ ...h, user_id: userId }));
    const { data } = await supabase.from('habits').insert(toInsert).select();
    setHabits((data ?? []) as Habit[]);
  }, [userId]);

  const updateWeekStartDay = useCallback(async (day: 0 | 1 | 2 | 3 | 4 | 5 | 6) => {
    if (!userId) return;
    setWeekStartDayState(day);
    await supabase.from('user_habit_settings').upsert(
      { user_id: userId, week_start_day: day, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' }
    );
  }, [userId]);

  const streak = useMemo(() => {
    if (habits.length === 0) return 0;
    let count = 0;
    for (let i = 1; i <= 12; i++) {
      const ws = getWeekStart(subWeeks(new Date(), i), weekStartDay);
      const allHit = habits.every(h => getCountForWeek(h.id, ws) >= h.target_per_week);
      if (allHit) count++;
      else break;
    }
    const currentWeekAllHit = habits.every(h => getCountForWeek(h.id, currentWeek) >= h.target_per_week);
    return count + (currentWeekAllHit ? 1 : 0);
  }, [habits, getCountForWeek, weekStartDay, currentWeek]);

  const previousPersonalBest = useMemo(() => {
    if (habits.length === 0 || logs.length === 0) return 0;
    const currentWeekStr = getWeekStart(new Date(), weekStartDay);
    const firstWeek = logs.reduce(
      (min, l) => (l.week_start < min ? l.week_start : min),
      logs[0].week_start,
    );
    let maxStreak = 0;
    let run = 0;
    let cursor = new Date(firstWeek + 'T00:00:00');
    while (true) {
      const ws = getWeekStart(cursor, weekStartDay);
      if (ws >= currentWeekStr) break;
      const allHit = habits.every(h => getCountForWeek(h.id, ws) >= h.target_per_week);
      if (allHit) {
        run++;
        if (run > maxStreak) maxStreak = run;
      } else {
        run = 0;
      }
      cursor = addDays(cursor, 7);
    }
    return maxStreak;
  }, [habits, logs, getCountForWeek, weekStartDay]);

  const isFirstWeekEver = useMemo(() => {
    if (habits.length === 0) return true;
    const currentWeekStr = getWeekStart(new Date(), weekStartDay);
    const pastWeeks = [...new Set(logs.map(l => l.week_start))].filter(w => w < currentWeekStr);
    return !pastWeeks.some(week =>
      habits.every(h => getCountForWeek(h.id, week) >= h.target_per_week),
    );
  }, [habits, logs, getCountForWeek, weekStartDay]);

  const ytdDealsAnalyzed = useMemo(() => {
    const ids = new Set(habits.filter(h => h.kpi_type === 'deals_analyzed').map(h => h.id));
    return logs.filter(l => ids.has(l.habit_id)).reduce((s, l) => s + (l.count ?? 0), 0);
  }, [habits, logs]);

  const ytdOffersSubmitted = useMemo(() => {
    const ids = new Set(habits.filter(h => h.kpi_type === 'offers_made').map(h => h.id));
    return logs.filter(l => ids.has(l.habit_id)).reduce((s, l) => s + (l.count ?? 0), 0);
  }, [habits, logs]);

  const weekProgress = useMemo(() => {
    if (habits.length === 0) return 0;
    const total = habits.reduce((sum, h) => sum + getCountForWeek(h.id, selectedWeek) / h.target_per_week, 0);
    return Math.round((total / habits.length) * 100);
  }, [habits, getCountForWeek, selectedWeek]);

  const availableWeeks = useMemo(() => {
    const weeks: string[] = [];
    for (let i = 0; i <= 12; i++) {
      weeks.push(getWeekStart(subWeeks(new Date(), i), weekStartDay));
    }
    return weeks;
  }, [weekStartDay]);

  const sortedHabits = useMemo(() => [...habits].sort((a, b) => a.sort_order - b.sort_order), [habits]);

  return {
    habits: sortedHabits, logs, loading, setupError,
    currentWeek, selectedWeek, setSelectedWeek, isCurrentWeek, availableWeeks,
    streak, weekProgress, previousPersonalBest, isFirstWeekEver, ytdDealsAnalyzed, ytdOffersSubmitted,
    weekStartDay, updateWeekStartDay,
    getCountForWeek, increment, decrement, logMinutes, setCount,
    updateHabit, reorderHabits, reorderHabitsToOrder, addHabit, removeHabit, resetToDefaults,
  };
}
